import Swup from 'swup'

/**
 * Swup wrapper. Everything outside #swup (header, footer, WebGL canvas) persists;
 * on every content swap the new page is described to the app via `onPage`.
 */
export default class PageTransitions {
	constructor({ onPage, beforeLeave, animateOut, animateIn, onBusy }) {
		// animationSelector false: no CSS transitions to wait for, the animate* callbacks drive it
		this.swup = new Swup({ animateHistoryBrowsing: true, animationSelector: false })
		// swup awaits async handlers here, so the out-animation waits on whatever this returns
		this.swup.hooks.on('visit:start', visit => beforeLeave?.(visit.to.url))
		this.swup.hooks.on('animation:out:await', () => animateOut?.())
		this.swup.hooks.on('animation:in:await', () => animateIn?.())
		this.swup.hooks.on('content:replace', () => onPage(PageTransitions.readPage()))
		this.swup.hooks.on('page:view', () => this.#markActiveMenuItem())

		this.#lockWhileNavigating(onBusy)
	}

	/** Whether a page transition is in progress (from the click until the new page is in). */
	get busy() {
		return document.documentElement.classList.contains('is-navigating')
	}

	// One transition at a time: busy cursor (css/records.css), link clicks swallowed, `onBusy` for the app
	#lockWhileNavigating(onBusy) {
		const root = document.documentElement
		const busy = on => {
			root.classList.toggle('is-navigating', on)
			onBusy?.(on)
		}
		this.swup.hooks.on('visit:start', () => busy(true), { before: true })
		this.swup.hooks.on('visit:end', () => busy(false))
		this.swup.hooks.on('visit:abort', () => busy(false))

		document.addEventListener(
			'click',
			event => {
				if (!this.busy || !event.target.closest('a[href]')) return
				event.preventDefault()
				event.stopImmediatePropagation()
			},
			{ capture: true }
		)
	}

	// The header lives outside #swup, so WordPress's current-menu-item class goes stale after a visit
	#markActiveMenuItem() {
		document.querySelectorAll('header .menu-item').forEach(item => {
			const link = item.querySelector('a')
			const isCurrent = link && new URL(link.href).pathname === window.location.pathname
			item.classList.toggle('current-menu-item', isCurrent)
			item.classList.remove('current-menu-ancestor', 'current_page_item')
		})
	}

	/** Describe the page currently inside #swup. */
	static readPage() {
		const container = document.querySelector('#swup')
		return {
			record: container.querySelector('[data-record]')?.dataset.record ?? null,
			slider: container.querySelector('[data-record-slider]'),
			anchor: container.querySelector('[data-record-anchor]'),
		}
	}
}
